/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect } from  'react';
import styled from 'styled-components';

import { dataLayer } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { translate } from '../../common/translation/translate';
import { IStoreState, OutdoorSportsSummaryResult } from '../common/interfaces/types';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { SafetyTipsLoadingUI } from './fallbackUI/safetyTipsLoadingUI';
import { OutdoorSportsLoadingErrorUI } from './fallbackUI/outdoorSportsLoadingErrorUI';
import { SafetyTipsInfo } from './safetyTipsInfo';
import { largeMediaQuery, mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { isDataError } from '../common/utilities/isDataError';
import { DisneyLogoImg, DisneyLogoWrapper, OutdoorSportsHeaderText, OutdoorSportsWithDisneySectionWrapper } from './commonStyles';

const { getOutdoorSportSummaryDataByLatLon } = dataLayer.actions;

interface OutdoorSportsSafetyTipsType {
    latitude: string;
    longitude: string;
    authId: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    outdoorSportsBaseUrl: string;
    getOutdoorSportSummaryDataByLatLon: typeof getOutdoorSportSummaryDataByLatLon;
    outdoorSportsSummaryData: OutdoorSportsSummaryResult;
    outdoorSportsDataIsFetching: boolean;
    isDesktop: boolean;
    safetyTipsIsLoading: boolean;
    error: Record<string, unknown>; 
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { outdoorSports } = storeState;
    const appState = storeState.appState.appState || {};
    const { authId, outdoorSportsBaseUrl, secret, stdParameters, user } = appState;
    const { error, outdoorSportsDataIsFetching, outdoorSportsSummaryData } = outdoorSports;

    return {
        latitude: user?.ActiveLocation?.Locality?.Latitude,
        longitude: user?.ActiveLocation?.Locality?.Longitude,
        authId,
        secret,
        stdParameters,
        outdoorSportsBaseUrl,
        outdoorSportsDataIsFetching,
        outdoorSportsSummaryData,
        error
    };
};

const mapDispatchToProps = { getOutdoorSportSummaryDataByLatLon };

export const OutdoorSportsSafetyTips = withScreenSize(reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    latitude,
    longitude,
    authId,
    secret,
    stdParameters,
    outdoorSportsBaseUrl,
    getOutdoorSportSummaryDataByLatLon,
    outdoorSportsDataIsFetching = false,
    outdoorSportsSummaryData,
    error
}: OutdoorSportsSafetyTipsType) => {

    const isApiError = isDataError(error);
    const fetchOutdoorSportsSummaryData = useCallback( async() => {
        try {
            if (latitude && longitude && authId && secret && stdParameters && outdoorSportsBaseUrl) {
                await getOutdoorSportSummaryDataByLatLon(
                    latitude,
                    longitude,
                    outdoorSportsBaseUrl, 
                    authId,
                    secret, 
                    stdParameters, 
                    true, 
                    false
                );
            }
        } catch (error) {
            console.log(error);
        }
    }, [latitude, longitude, outdoorSportsBaseUrl, authId, secret]);

    useEffect(() => {
        fetchOutdoorSportsSummaryData();
    }, [fetchOutdoorSportsSummaryData]);

    return (
        <Container>
            <OutdoorSportsWithDisneySectionWrapper>
                <OutdoorSportsHeaderText>{`${translate('safetyTips')}`}</OutdoorSportsHeaderText>
                <DisneyLogoWrapper href='https://disneysynclink.com/weathermonitoring/' target='_blank'>
                    <DisneyLogoImg alt='Disney Weather Check' src='/images/disney-weather-check-logo.png' />
                </DisneyLogoWrapper>
            </OutdoorSportsWithDisneySectionWrapper>
            
            { outdoorSportsDataIsFetching && <SafetyTipsLoadingUI /> }
            { !outdoorSportsDataIsFetching && !outdoorSportsSummaryData && isApiError && <OutdoorSportsLoadingErrorUI /> }
            { !outdoorSportsDataIsFetching && outdoorSportsSummaryData && !isApiError
                && <SafetyTipsInfo
                    backgroundFillColor={outdoorSportsSummaryData?.safetyTip?.color}
                    indexNumber={outdoorSportsSummaryData?.safetyTip?.indexNumber.toString()}
                    indexLabel={outdoorSportsSummaryData?.safetyTip?.indexLabel}
                    safetyTips={outdoorSportsSummaryData?.safetyTip?.safetyTips}
                />
            }
        </Container>
    );
})));

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #F9F9F9;
    height: 420px;
    width: 100%;

    @media ${largeMediaQuery} {
        width: 40%;
    }

    @media ${mediumMediaQuery} {
        height: 170px;
    }

    @media ${smallMediaQuery} {
        height: 220px;
    }
`;
